import { type PortalOptionItem } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import date from "@common/lib/date";
import { usePromise } from "@common/lib/hooks";
import getOptions from "@common/lib/options";
import { getApi } from "@common/modules/data/api";
import { type Data } from "@common/modules/data/types";
import { Common } from "@common/types";
import React from "react";
import { type ProviderProps } from "../types";
import CommonStaticProvider from "./CommonStaticProvider";

export type TrafficByUrl = {
  url: string;
  visitors: number;
  sessions: number;
  views: number;
};

export type TrafficByUrlRequest = Common.Api.PageRequest & {
  startDate: Date;
  endDate: Date;
  portalId: string[];
};
export type Props = Omit<ProviderProps, "id">;
export const WEB_TRAFFIC_BY_URL_ID = "12b9601f-2c3a-421c-a40b-5a9e465c7aec";

const TrafficByUrlProvider = (props: Props) => {
  const i18n = useI18n();

  const api = React.useMemo(() => getApi(i18n), [i18n]);
  const options = React.useMemo(() => getOptions(i18n), [i18n]);

  const [portals, isPortalsLoading] = usePromise<PortalOptionItem[]>({
    fn: options.getCustomOptions,
    args: ["web", "portals"],
    shouldLoad: true
  });

  const fields = React.useMemo(
    (): Data.Api.FormField[] => [
      {
        formControlKey: "DateRange",
        formFieldId: "dateRange",
        formFieldKey: "Date",
        label: i18n.dateRange,
        plainTextValue: `{"from": "${date.extractDate(date.addYears(new Date(), -1))}","to": "${date.extractDate(
          new Date()
        )}"}`
      },
      {
        formControlKey: "Select",
        formFieldId: "portal",
        formFieldKey: "PortalId",
        label: i18n.portal,
        source: "api:web|portals",
        plainTextValue: (portals ?? []).find((x) => x.isDefault)?.value ?? (portals ?? [])[0]?.value,
        isRequired: true
      }
    ],
    [i18n, portals]
  );

  const getChartGridData = React.useCallback((id, data) => api.getTrafficByUrl(data as any), []);

  return (
    <CommonStaticProvider
      badges={["StartDate", "PortalId"]}
      category="Website"
      fields={fields}
      isLoading={isPortalsLoading}
      onLoaded={props.onLoaded}
    >
      {({ getBadges, getFilters, parameters }) =>
        props.children({
          id: WEB_TRAFFIC_BY_URL_ID,
          config: {
            aspectRatio: 2.5,
            colors: [],
            xAxis: {
              key: "url"
            },
            yAxis: [
              { key: "views", color: "{$Blue}", label: i18n.views },
              { key: "sessions", color: "{$Green}", label: i18n.sessions },
              { key: "visitors", color: "{$Purple}", label: i18n.visitors }
            ],
            totalRecords: 25,
            yNoData: "hide"
          },
          fields,
          hasGrid: true,
          isLoading: isPortalsLoading,
          name: i18n.trafficByUrl,
          parameters,
          type: "Bar",
          getBadges,
          getChartData: getChartGridData,
          getFilters: (id, v) => getFilters(id, v, portals ?? []),
          gridConfig: { columns: [], pageSize: 25, key: "url" },
          gridColumns: (data) => [
            {
              dataType: "string",
              key: "url",
              label: i18n.url
            },
            {
              dataType: "number",
              key: "views",
              label: i18n.views
            },
            {
              dataType: "number",
              key: "sessions",
              label: i18n.sessions
            },
            {
              dataType: "number",
              key: "visitors",
              label: i18n.visitors
            }
          ]
        })
      }
    </CommonStaticProvider>
  );
};

export default TrafficByUrlProvider;
